// Package 4, part I (owner, 7. 10. 2026; the world book, "Lanci priče: sezona 1"): three story chains answer the season's
// question (why everyone turned on the same day) from three sides: the radio (Dead Air, Danny Dawn), the hospital
// (Patient 07, Doc Mara) and WARDEN (Off the Grid, Elias Crane and Pidge). A step opens when the one before is done and
// the survivor has the level for the step's zone, so a chain lasts the season. A step in a zone comes as its own event
// on a run there, its chance growing with every run there without it; some steps are done at Station 9 (the radio, a
// delivery). A finished chain gives a title, a cosmetic and 5 Relic Shards; nothing that adds combat strength. In the
// test the chains run in Season 0.

import { ZONES } from './content/zones';
import { EVENT_BY_ID, type ExpeditionEvent } from './events';
import type { NamedId } from './named';
import type { StackMap, ZoneId } from './types';

export type ChainId = 'dead_air' | 'patient07' | 'off_the_grid';

export interface StoryStep {
  /** Where: a zone's run, Station 9 (`camp`), or any run (`any`). */
  zone: ZoneId | 'camp' | 'any';
  /** The step's name on the Camp card and in the Collection ("The studio"). */
  title: string;
  /** The world book's line, said when the step is done. */
  line: string;
  /** What to do, on the Camp card ("Runs in Downtown", "Bring Doc Mara 20 Medicine"). */
  hint: string;
  /** The step's event on a run (its options; a branch with `story: true` does the step). */
  event?: ExpeditionEvent;
  /** Delivered from Storage at Station 9 (after the event, when the step has one). */
  deliver?: StackMap;
  /** Station 9's radio: listened to on the Camp card. */
  listen?: boolean;
  /** Beat this named enemy (its chance in the zone ×`STORY_RULES.namedMult` while the step is open). */
  named?: NamedId;
  /** The event this many times (one each time). */
  count?: number;
  /** WARDEN's checkpoint: passed only without a fever or an injury (else the event says so and the step waits). */
  clean?: boolean;
}

export interface StoryChain {
  id: ChainId;
  name: string;
  /** The character whose chain it is. */
  who: string;
  steps: StoryStep[];
  /** `cosmeticArt`: the cosmetic's picture (art folder `cosmetics`, 8. 10. 2026; an icon in the Collection for now). */
  reward: { title: string; achievement: string; cosmetic: string; cosmeticArt: string; shards: number };
}

const ALWAYS = { peaceful: 'go', cautious: 'go', aggressive: 'go', shadow: 'go' };
/** A step's event with one option that does it. */
function ev(id: string, zone: ZoneId | 'any', title: string, text: string, label: string, done: string, xp = 20): ExpeditionEvent {
  return { id, zones: [zone], title, text, options: [{ id: 'go', label, safe: true, effects: { text: done, xp, story: true } }], stanceDefault: ALWAYS };
}

export const STORY_CHAINS: Record<ChainId, StoryChain> = {
  dead_air: {
    id: 'dead_air',
    name: 'Dead Air',
    who: 'Danny Dawn',
    reward: { title: 'Dead Air', achievement: 'story_dead_air', cosmetic: 'Dispatcher’s Headset', cosmeticArt: 'cosmetic_dispatchers_headset', shards: 5 },
    steps: [
      { zone: 'camp', title: 'Numbers', line: 'Under Danny’s voice, someone is reading numbers. Slowly. Like they’re for you.', hint: 'Listen to the radio at Station 9', listen: true },
      {
        zone: 'downtown',
        title: 'The studio',
        line: 'The studio light is on. The chair is warm. Nobody’s here.',
        hint: 'Runs in Downtown: the old radio studio',
        event: ev('story_dead_air_2', 'downtown', 'The Radio Studio', 'The local station still runs on solar panels. The studio light is on. The chair is warm. Nobody’s here.', 'Look around', 'Tapes, notes, a mug still warm. Someone left in a hurry.'),
      },
      {
        zone: 'downtown',
        title: 'The tapes',
        line: '"Phase Two begins at sunrise." The date is the Last Day.',
        hint: 'Runs in Downtown: the tape archive',
        event: ev('story_dead_air_3', 'downtown', 'The Tape Archive', 'Boxes of MEND ad scripts. One broadcast schedule is different. "Phase Two begins at sunrise." The date is the Last Day.', 'Take the schedule', 'You fold it twice and keep it close.', 30),
      },
      {
        zone: 'gas_station',
        title: 'The toll log',
        line: 'Everything on Route 9 has a price. Even old paperwork.',
        hint: 'Runs on Route 9: a Tollman has the radio log',
        event: {
          id: 'story_dead_air_4',
          zones: ['gas_station'],
          title: 'The Toll Log',
          text: 'A Tollman sits on a crate with a radio logbook on his knees. Everything on Route 9 has a price. Even old paperwork.',
          options: [
            { id: 'pay', label: 'Pay 40 Scrap', cost: { scrap: 40 }, effects: { text: 'He tears out the pages and counts the Scrap twice.', xp: 20, story: true } },
            { id: 'fight', label: 'Take it', effects: { text: 'He doesn’t sell. You don’t ask twice.', fight: { enemy: 'raider', tierOffset: 1, onWin: { xp: 40 } }, story: true } },
            { id: 'walk', label: 'Walk on', safe: true, effects: { text: 'Some other day. The log isn’t going anywhere.' } },
          ],
          stanceDefault: { peaceful: 'pay', cautious: 'pay', aggressive: 'fight', shadow: 'pay' },
        },
      },
      {
        zone: 'military_base',
        title: 'The relay',
        line: 'Halcyon wrote the message. The city delivered it.',
        hint: 'Runs in Fort Halden: the relay mast',
        event: ev('story_dead_air_5', 'military_base', 'The Relay Mast', 'A relay mast carries two logos, Halcyon and WARDEN. The signal went out over the city’s own network. Halcyon wrote the message. The city delivered it.', 'Copy the logs', 'The logs fit on one page. That is the worst part.', 40),
      },
    ],
  },
  patient07: {
    id: 'patient07',
    name: 'Patient 07',
    who: 'Doc Mara',
    reward: { title: 'Trial Subject', achievement: 'story_patient07', cosmetic: 'Doc’s Coat', cosmeticArt: 'cosmetic_docs_coat', shards: 5 },
    steps: [
      {
        zone: 'hospital',
        title: 'The intake tent',
        line: 'Doc Mara doesn’t look up. "If you’re bleeding, sit. If you’re not, help."',
        hint: 'Runs in Mercy General: the intake tent',
        event: ev('story_patient07_1', 'hospital', 'The Intake Tent', 'A woman in a stained coat works the intake tent alone. Doc Mara doesn’t look up. "If you’re bleeding, sit. If you’re not, help."', 'Help her', 'You carry, hold and clean. She never says thanks. She remembers your name.'),
      },
      { zone: 'camp', title: 'Twenty doses', line: '"Twenty doses. Don’t ask what for. You won’t like it."', hint: 'Bring Doc Mara 20 Medicine', deliver: { medicine: 20 } },
      {
        zone: 'hospital',
        title: 'The fever logs',
        line: 'Every fever log ends the same way: "Patient stable. Patient improving."',
        hint: 'Runs in Mercy General: the basement',
        event: ev('story_patient07_3', 'hospital', 'The Fever Logs', 'Folders from the first trial patients, stacked in the basement. Every fever log ends the same way: "Patient stable. Patient improving."', 'Take the logs', 'You take the whole box. Doc Mara will want to read them twice.', 30),
      },
      {
        zone: 'hospital',
        title: 'Patient 07',
        line: 'The wristband reads: Trial 07. Batch 1. Dose date matches yours, if you had one.',
        hint: 'Beat Patient 07 in Mercy General',
        named: 'patient07',
      },
      {
        zone: 'mountains',
        title: 'The locked wing',
        line: 'One batch. One schedule. One morning.',
        hint: 'Runs in Pine Ridge: the sanatorium’s locked wing',
        event: ev('story_patient07_5', 'mountains', 'The Locked Wing', 'A letter in the sanatorium’s locked wing. Every dose came from the same batch, with an "activation" already scheduled. One batch. One schedule. One morning.', 'Read it', 'You read it twice. Then you put it somewhere the drones won’t look.', 40),
      },
      {
        zone: 'hospital',
        title: 'The signature',
        line: '"I signed the trials. So yes. This is partly my handwriting."',
        hint: 'Runs in Mercy General: Doc Mara',
        event: ev('story_patient07_6', 'hospital', 'Doc Mara', 'Doc Mara reads the letter and sits down for the first time. "I signed the trials. So yes. This is partly my handwriting."', 'Stay with her', 'She doesn’t ask you to stay. You stay anyway.', 40),
      },
    ],
  },
  off_the_grid: {
    id: 'off_the_grid',
    name: 'Off the Grid',
    who: 'Elias Crane and Pidge',
    reward: { title: 'Off the Grid', achievement: 'story_off_the_grid', cosmetic: 'Pidge, a small drone that follows you', cosmeticArt: 'cosmetic_pidge', shards: 5 },
    steps: [
      {
        zone: 'any',
        title: 'A note',
        line: 'A note drops from the sky: "Don’t run. It’s counting."',
        hint: 'Any run: something is following you',
        event: ev('story_off_the_grid_1', 'any', 'A Note From the Sky', 'A small drone follows you for a while, too high to hit. Then a note drops from the sky: "Don’t run. It’s counting."', 'Keep the note', 'The drone tilts, almost a nod, and leaves.'),
      },
      {
        zone: 'downtown',
        title: 'Parking fines',
        line: 'Fine: $40. Reason: standing. Case number: 7741.',
        hint: 'Runs in Downtown: tickets from WARDEN’s Patrol drones',
        count: 5,
        event: ev('story_off_the_grid_2', 'downtown', 'Parking Fine', 'A WARDEN Patrol drone stops, scans you and prints a ticket. Fine: $40. Reason: standing. Case number: 7741.', 'Take the ticket', 'Every ticket has a case number. Someone at WARDEN is still keeping files.'),
      },
      {
        zone: 'forest',
        title: 'Elias',
        line: '"Pidge likes you. Pidge doesn’t like anyone. Bring me wire."',
        hint: 'Runs in Calder Woods, then bring Elias 10 Electronics',
        deliver: { electronics: 10 },
        event: ev('story_off_the_grid_3', 'forest', 'Elias Crane', 'A man in an old WARDEN jacket sits by a small fire, a drone on his shoulder. "Pidge likes you. Pidge doesn’t like anyone. Bring me wire."', 'Promise him the wire', 'He wants Electronics. Ten will do, he says. He says it like a work order.'),
      },
      {
        zone: 'military_base',
        title: 'The checkpoint',
        line: 'The gate scans you. 36.8 °C. "Thank you for your compliance."',
        hint: 'Runs in Fort Halden, without a fever or an injury',
        clean: true,
        event: ev('story_off_the_grid_4', 'military_base', 'The Checkpoint', 'A WARDEN checkpoint blocks the way in. The gate scans you. 36.8 °C. "Thank you for your compliance."', 'Walk through', 'The barrier lifts. Nobody is there to see it.'),
      },
      {
        zone: 'military_base',
        title: 'The protocol log',
        line: 'Final Protocol: CONFIRMED. User: H—. The rest is burned out.',
        hint: 'Runs in Fort Halden: the control center',
        event: ev('story_off_the_grid_5', 'military_base', 'The Protocol Log', 'A terminal in the control center still prints logs. Final Protocol: CONFIRMED. User: H—. The rest is burned out.', 'Copy what’s left', 'Someone pressed the button by hand. Elias will want the letter H.', 40),
      },
    ],
  },
};

/** WARDEN's checkpoint with a fever or an injury: the scan fails and the step waits. */
export const CHECKPOINT_FAIL: ExpeditionEvent = {
  id: 'story_off_the_grid_4x',
  zones: ['military_base'],
  title: 'The Checkpoint',
  text: 'A WARDEN checkpoint blocks the way in. The gate scans you. 38.4 °C. "Please remain where you are."',
  options: [{ id: 'go', label: 'Back away', safe: true, effects: { text: 'You leave before the others arrive. Come back without a fever, and without a wound.' } }],
  stanceDefault: ALWAYS,
};

export const STORY_RULES = {
  on: true,
  /** A step's event: this chance on a run in its zone, + this much for every run there without it (at most 1). */
  baseChance: 0.15,
  perRun: 0.15,
  /** The named enemy of an open step comes this many times as often in its zone. */
  namedMult: 3,
};

export const CHAIN_IDS = Object.keys(STORY_CHAINS) as ChainId[];

/** One chain's progress: the next step (0-based), its count (fines, Elias met), runs in its zone without it, when done. */
export interface ChainState {
  step: number;
  progress?: number;
  runsWithout?: number;
  done?: number;
}
export type StoryState = Partial<Record<ChainId, ChainState>>;

/** The chance of a step's event on a run in its zone. */
export const storyChance = (runsWithout: number) => Math.min(1, STORY_RULES.baseChance + STORY_RULES.perRun * Math.max(0, runsWithout));

/** A step is open when the survivor has the level for its zone (Station 9 and any run: always). */
export const stepOpen = (step: StoryStep, level: number) => step.zone === 'camp' || step.zone === 'any' || level >= ZONES[step.zone].unlockLevel;

/** The step a chain is on (null: done). */
export function currentStep(chain: ChainId, st: ChainState | undefined): StoryStep | null {
  const i = st?.step ?? 0;
  return st?.done ? null : (STORY_CHAINS[chain].steps[i] ?? null);
}

/** The step's event still to come on a run (a delivery step with an event: until Elias is met). */
export const stepNeedsEvent = (step: StoryStep, st: ChainState | undefined) => !!step.event && !(step.deliver && (st?.progress ?? 0) >= 1);

/** A delivery waits at Station 9 (a step with an event: once the event is done). */
export const stepNeedsDelivery = (step: StoryStep, st: ChainState | undefined) => !!step.deliver && (!step.event || (st?.progress ?? 0) >= 1);

/** Every step's event, by id (the server finds an answered one by it). */
export const STORY_EVENTS: Record<string, ExpeditionEvent> = Object.fromEntries([...CHAIN_IDS.flatMap((c) => STORY_CHAINS[c].steps.flatMap((s) => (s.event ? [[s.event.id, s.event]] : []))), [CHECKPOINT_FAIL.id, CHECKPOINT_FAIL]]);

/** Which chain and step an event belongs to. */
export function storyStepOf(eventId: string): { chain: ChainId; index: number } | null {
  for (const c of CHAIN_IDS) {
    const index = STORY_CHAINS[c].steps.findIndex((s) => s.event?.id === eventId || (s.clean && eventId === CHECKPOINT_FAIL.id));
    if (index >= 0) return { chain: c, index };
  }
  return null;
}

// The server finds an answered event by its id: the steps' events join the book of events (never the zones' pools).
Object.assign(EVENT_BY_ID, STORY_EVENTS);
